/** Shared design foundations used across design system tabs. */
import { useState } from 'react';

// ─── Hover helper ─────────────────────────────────────────────────────────────

export function useHover(): [boolean, { onMouseEnter: () => void; onMouseLeave: () => void }] {
  const [h, setH] = useState(false);
  return [h, { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) }];
}

// ─── Surface constants ────────────────────────────────────────────────────────

/** Standard container surface — all cards, panels, and list containers. */
export const SURFACE: React.CSSProperties = {
  borderRadius: 14,
  background: 'var(--color-surface)',
  backdropFilter: 'blur(16px) saturate(1.3)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
  border: '1px solid var(--color-surface-border)',
  boxShadow:
    'inset 0 0.5px 0 var(--color-surface-highlight), 0 1px 3px rgba(0,0,0,0.03), 0 2px 8px rgba(0,0,0,0.02)',
};

export const SHADOW_REST =
  'inset 0 0.5px 0 var(--color-surface-highlight), 0 1px 3px rgba(0,0,0,0.03), 0 2px 8px rgba(0,0,0,0.02)';
export const SHADOW_HOVER =
  'inset 0 0.5px 0 var(--color-surface-highlight), 0 2px 8px rgba(0,0,0,0.06)';

/**
 * Standard interactive card hover style.
 * Apply via onMouseEnter/onMouseLeave or the useHover() hook.
 *
 * Rest:  border: 1px solid var(--color-surface-border), boxShadow: SHADOW_REST
 * Hover: borderColor: var(--color-border-strong),   boxShadow: SHADOW_HOVER
 *
 * No translateY — elevation is communicated through border + shadow only.
 */
export const HOVER_BORDER_REST = 'var(--color-surface-border)';
export const HOVER_BORDER_HOVER = 'var(--color-border-strong)';

/** Progress bar track colour. */
export const TRACK = 'var(--color-surface-track)';

// ─── Typography ───────────────────────────────────────────────────────────────
// All weights use clean stops from --weight-* tokens.
// Never use intermediate values (430, 480, 550, 620, 680…).

/** Eyebrow / overline — section labels, card category tags, metric tile headers. */
export const eyebrow: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600, // --weight-semibold
  letterSpacing: '0.08em', // --tracking-eyebrow — decisive, premium
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

/** Card / component title — course cards, surface headings, list item titles. */
export const title: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600, // --weight-semibold
  letterSpacing: '-0.01em', // --tracking-snug
  color: 'var(--color-text)',
  lineHeight: 1.35, // --leading-snug
};

/** Supporting body copy — descriptions, rule text, explainer paragraphs. */
export const body: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 400, // --weight-normal — pure regular, not "warm medium"
  color: 'var(--color-text-secondary)',
  lineHeight: 1.65, // generous reading rhythm
};

/** Small metadata — timestamps, counts, secondary labels on cards. */
export const meta: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 400, // --weight-normal — subdued but legible
  color: 'var(--color-text-muted)',
  lineHeight: 1.4,
};

/** Page title (h1) — top-level page headings, uses display font. */
export const pageTitle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-3xl)',
  fontWeight: 700, // --weight-bold
  letterSpacing: 'var(--tracking-tight)',
  lineHeight: 'var(--leading-tight)',
  color: 'var(--color-text)',
};

/** Section heading (h2) — within-page sections, uses display font. */
export const sectionHeading: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-xl)',
  fontWeight: 600, // --weight-semibold
  letterSpacing: 'var(--tracking-snug)',
  lineHeight: 'var(--leading-snug)',
  color: 'var(--color-text)',
};

/** Helper text — input descriptions, hints, field-level guidance. */
export const helperText: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 400, // --weight-normal
  letterSpacing: 0,
  color: 'var(--color-text-secondary)',
  lineHeight: 1.5,
};

/** Utility / chip text — tags, badges, pill labels. */
export const utilityText: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500, // --weight-medium
  letterSpacing: '0.02em',
  color: 'var(--color-text-secondary)',
};

/** Button label — all button text regardless of size. */
export const buttonLabel: React.CSSProperties = {
  fontWeight: 600, // --weight-semibold
  letterSpacing: '-0.005em',
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

export { default as Section } from './Section';
